// Regulated verdicts (TASK-703) — the durable result of compliance / risk specialist work.
//
// Read-only on purpose. The gateway proxies the GET and NOT the POST: a verdict is written by
// a deterministic engine over the internal agent path, and the browser must be able to read
// one (TASK-702 opens it from the review card) without ever being able to author one.
import { httpClient } from '../../lib/httpClient';

export interface RegulatedVerdict {
  id: string;
  client_id: string;
  task_id: string | null;
  /** suitability_assessment | compliance_review */
  verdict_type: string;
  /** The deterministic engine that decided — never a model. */
  engine: string;
  engine_version: string | null;
  inputs: Record<string, unknown>;
  outcome: string;
  result: Record<string, unknown>;
  reasons: unknown[];
  checks: unknown[];
  failures: unknown[];
  actor: string;
  actor_kind: string;
  created_at: string | null;
  /** The verdict this one corrects; a correction is a new row, never an edit. */
  supersedes_id: string | null;
  /**
   * TASK-931: the LATER verdict that replaced this one, when the client's risk profile has
   * been completed again since. The opposite direction to `supersedes_id`, and the one a
   * reader of an old verdict needs: without it the page renders a green `pass` that is an
   * assessment of a profile the client has since replaced.
   *
   * Absent while this verdict is still current — absence means current, never unknown.
   */
  superseded_by?: {
    verdict_id: string;
    outcome: string;
    risk_profile_version: number | null;
    at: string | null;
    /** Server-composed, so two surfaces cannot word it differently. */
    label: string;
  } | null;
}

export const verdictApiService = {
  async getVerdict(verdictId: string): Promise<RegulatedVerdict> {
    const data = await httpClient.get<{ success: boolean; verdict: RegulatedVerdict }>(
      `/api/regulated-verdicts/${verdictId}`
    );
    return data.verdict;
  },
};
